import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Slider, Select, Switch, Typography, Spin, Input } from 'antd';
import apiClient from '../../../api/client';

const { Text } = Typography;

/**
 * PatternEditorPanel — Parametric geometric pattern generator for the Scrapbook.
 * 
 * Generates math-driven patterns (spirals, concentrics, grids, waves, radial mandalas,
 * moirés) with configurable fill styles (solid, dots, crosshatch, dither, noise).
 * 
 * Can operate in two modes:
 *  - Effect mode: composites pattern onto an existing layer (sourceImageUrl provided)
 *  - Standalone mode: generates pattern as new layer (sourceImageUrl is null)
 * 
 * Props:
 *  - visible, onClose
 *  - onApply(dataUrl) — effect mode: replace layer image
 *  - onAddAsLayer(dataUrl) — standalone mode: add as new scrapbook layer
 *  - sourceImageUrl, proxyUrl — for effect mode overlay
 *  - canvasId — for palette fetching
 */

const GEOMETRY_MODES = [
    { value: 'spiral', label: 'Spiral', icon: '🌀' },
    { value: 'concentric', label: 'Concentric', icon: '◎' },
    { value: 'grid', label: 'Grid', icon: '▦' },
    { value: 'wave', label: 'Wave', icon: '〰' },
    { value: 'radial', label: 'Radial', icon: '✺' },
    { value: 'moire', label: 'Moiré', icon: '◉' },
];

const FILL_STYLES = [
    { value: 'solid', label: 'Solid' },
    { value: 'dots', label: 'Dots' },
    { value: 'crosshatch', label: 'Crosshatch' },
    { value: 'dither', label: 'Dither' },
    { value: 'noise', label: 'Noise' },
];

const BLEND_MODES = [
    { value: 'source-over', label: 'Normal' },
    { value: 'multiply', label: 'Multiply' },
    { value: 'screen', label: 'Screen' },
    { value: 'overlay', label: 'Overlay' },
    { value: 'soft-light', label: 'Soft Light' },
];

const PatternEditorPanel = ({ visible, onClose, onApply, onAddAsLayer, sourceImageUrl, proxyUrl, canvasId }) => {
    const previewCanvasRef = useRef(null);
    const sourceImgRef = useRef(null);
    const processingRef = useRef(false);
    const pendingRef = useRef(false);

    // Geometry
    const [geometryMode, setGeometryMode] = useState('spiral');
    const [frequency, setFrequency] = useState(12);
    const [rotation, setRotation] = useState(0);
    const [scale, setScale] = useState(1.0);
    const [lineWeight, setLineWeight] = useState(2);
    const [symmetry, setSymmetry] = useState(6);
    const [amplitude, setAmplitude] = useState(0.5);
    const [wavelength, setWavelength] = useState(0.4);
    const [twist, setTwist] = useState(3);
    const [arms, setArms] = useState(5);
    const [jitter, setJitter] = useState(0);

    // Fill
    const [fillStyle, setFillStyle] = useState('solid');
    const [fillDensity, setFillDensity] = useState(0.5);

    // Colors
    const [fgColor, setFgColor] = useState('#c084fc');
    const [bgColor, setBgColor] = useState('#0d1117');
    const [transparentBg, setTransparentBg] = useState(false);
    const [useGradient, setUseGradient] = useState(false);
    const [gradientEnd, setGradientEnd] = useState('#6d28d9');
    const [gradientAngle, setGradientAngle] = useState(135);

    // Blend (effect mode)
    const [blendMode, setBlendMode] = useState('source-over');
    const [blendOpacity, setBlendOpacity] = useState(0.85);

    // Randomization
    const [seed, setSeed] = useState(Date.now());

    // Fill tool
    const [fillToolActive, setFillToolActive] = useState(false);
    const [fillOperations, setFillOperations] = useState([]);
    const [fillColor, setFillColor] = useState('#c084fc');
    const fillOpsRef = useRef([]);

    // Palettes
    const [palettes, setPalettes] = useState([]);

    const [loading, setLoading] = useState(false);
    const isEffectMode = !!sourceImageUrl;

    // Load source image for effect mode
    useEffect(() => {
        if (!visible) return;
        if (sourceImageUrl) {
            setLoading(true);
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { sourceImgRef.current = img; setLoading(false); };
            img.onerror = () => { sourceImgRef.current = null; setLoading(false); };
            img.src = proxyUrl || sourceImageUrl;
        } else {
            sourceImgRef.current = null;
        }
    }, [visible, sourceImageUrl, proxyUrl]);

    // Fetch palettes (only when we have a valid canvasId)
    useEffect(() => {
        if (!visible || !canvasId) return;
        const fetchPalettes = async () => {
            try {
                const res = await apiClient.get(`/artboards/${canvasId}/palettes`);
                if (res.data?.palettes) {
                    setPalettes(res.data.palettes);
                }
            } catch (err) {
                // Silently ignore — palettes are optional
            }
        };
        fetchPalettes();
    }, [visible, canvasId]);

    // Re-render on any param change
    useEffect(() => {
        if (!visible) return;
        if (isEffectMode && !sourceImgRef.current && loading) return;
        renderPattern();
    }, [visible, geometryMode, frequency, rotation, scale, lineWeight, symmetry,
        amplitude, wavelength, twist, arms, jitter, fillStyle, fillDensity,
        fgColor, bgColor, transparentBg, useGradient, gradientEnd, gradientAngle, blendMode, blendOpacity,
        seed, loading, fillOperations]);

    // Seeded PRNG
    const seededRandom = (s) => {
        let state = s;
        return () => {
            state = (state * 1103515245 + 12345) & 0x7fffffff;
            return state / 0x7fffffff;
        };
    };

    const renderPattern = useCallback(() => {
        if (!previewCanvasRef.current) return;
        if (processingRef.current) { pendingRef.current = true; return; }
        processingRef.current = true;
        requestAnimationFrame(() => {
            try {
                doRender(previewCanvasRef.current, 800);
            } finally {
                processingRef.current = false;
                if (pendingRef.current) { pendingRef.current = false; renderPattern(); }
            }
        });
    }, [geometryMode, frequency, rotation, scale, lineWeight, symmetry,
        amplitude, wavelength, twist, arms, jitter, fillStyle, fillDensity,
        fgColor, bgColor, transparentBg, useGradient, gradientEnd, gradientAngle, blendMode, blendOpacity, seed]);

    // ==================== RENDER ENGINE ====================

    const doRender = (canvas, maxDim) => {
        let w, h;
        const img = sourceImgRef.current;

        if (img) {
            const s = Math.min(1, maxDim / Math.max(img.width, img.height));
            w = Math.round(img.width * s);
            h = Math.round(img.height * s);
        } else {
            w = maxDim;
            h = maxDim;
        }

        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        // Draw background / source image
        if (img) {
            ctx.drawImage(img, 0, 0, w, h);
        } else if (!transparentBg) {
            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, w, h);
        }

        // Create pattern on offscreen canvas
        const patternCanvas = document.createElement('canvas');
        patternCanvas.width = w;
        patternCanvas.height = h;
        const pCtx = patternCanvas.getContext('2d');

        // Build color/gradient
        const getStroke = () => {
            if (useGradient) {
                const angleRad = (gradientAngle * Math.PI) / 180;
                const halfW = w / 2;
                const halfH = h / 2;
                const len = Math.sqrt(halfW * halfW + halfH * halfH);
                const x1 = halfW - Math.cos(angleRad) * len;
                const y1 = halfH - Math.sin(angleRad) * len;
                const x2 = halfW + Math.cos(angleRad) * len;
                const y2 = halfH + Math.sin(angleRad) * len;
                const grad = pCtx.createLinearGradient(x1, y1, x2, y2);
                grad.addColorStop(0, fgColor);
                grad.addColorStop(1, gradientEnd);
                return grad;
            }
            return fgColor;
        };

        pCtx.strokeStyle = getStroke();
        pCtx.fillStyle = getStroke();
        pCtx.lineWidth = lineWeight;
        pCtx.lineCap = 'round';
        pCtx.lineJoin = 'round';

        const cx = w / 2;
        const cy = h / 2;
        const maxR = Math.sqrt(cx * cx + cy * cy) * scale;
        const rng = seededRandom(seed);
        const rotRad = (rotation * Math.PI) / 180;

        // Geometry dispatch
        switch (geometryMode) {
            case 'spiral':
                drawSpiral(pCtx, cx, cy, maxR, rotRad, rng, w, h);
                break;
            case 'concentric':
                drawConcentric(pCtx, cx, cy, maxR, rotRad, rng, w, h);
                break;
            case 'grid':
                drawGrid(pCtx, cx, cy, maxR, rotRad, rng, w, h);
                break;
            case 'wave':
                drawWave(pCtx, cx, cy, maxR, rotRad, rng, w, h);
                break;
            case 'radial':
                drawRadial(pCtx, cx, cy, maxR, rotRad, rng, w, h);
                break;
            case 'moire':
                drawMoire(pCtx, cx, cy, maxR, rotRad, rng, w, h);
                break;
            default:
                drawSpiral(pCtx, cx, cy, maxR, rotRad, rng, w, h);
        }

        // Composite pattern onto main canvas
        ctx.save();
        if (img) {
            ctx.globalCompositeOperation = blendMode;
            ctx.globalAlpha = blendOpacity;
        }
        ctx.drawImage(patternCanvas, 0, 0);
        ctx.restore();

        // Re-apply stored fill operations
        reapplyFills(ctx, w, h);

        // Preserve original alpha if in effect mode
        if (img) {
            const alphaCanvas = document.createElement('canvas');
            alphaCanvas.width = w;
            alphaCanvas.height = h;
            const alphaCtx = alphaCanvas.getContext('2d');
            alphaCtx.drawImage(img, 0, 0, w, h);
            ctx.globalCompositeOperation = 'destination-in';
            ctx.drawImage(alphaCanvas, 0, 0);
            ctx.globalCompositeOperation = 'source-over';
        }
    };

    // ==================== GEOMETRY ENGINES ====================

    const drawSpiral = (ctx, cx, cy, maxR, rot, rng, w, h) => {
        const armCount = arms;
        const twistFactor = twist * 0.5;
        const spacing = maxR / (frequency * 2);
        const jitterAmt = jitter * spacing * 0.3;

        for (let a = 0; a < armCount; a++) {
            const armAngle = (a / armCount) * Math.PI * 2 + rot;
            ctx.beginPath();
            let first = true;
            for (let t = 0; t < frequency * 80; t++) {
                const theta = armAngle + (t * 0.02 * twistFactor);
                const r = t * spacing * 0.02;
                if (r > maxR) break;

                let x = cx + Math.cos(theta) * r;
                let y = cy + Math.sin(theta) * r;

                if (jitterAmt > 0) {
                    x += (rng() - 0.5) * jitterAmt;
                    y += (rng() - 0.5) * jitterAmt;
                }

                if (first) { ctx.moveTo(x, y); first = false; }
                else { ctx.lineTo(x, y); }
            }
            ctx.stroke();
        }

        // Fill between arms with secondary detail lines
        if (fillDensity > 0) {
            ctx.save();
            ctx.globalAlpha = fillDensity * 0.3;
            ctx.lineWidth = Math.max(0.5, lineWeight * 0.3);
            for (let a = 0; a < armCount; a++) {
                const armAngle = (a / armCount) * Math.PI * 2 + rot + (Math.PI / armCount);
                ctx.beginPath();
                let first = true;
                for (let t = 0; t < frequency * 60; t++) {
                    const theta = armAngle + (t * 0.02 * twistFactor);
                    const r = t * spacing * 0.02;
                    if (r > maxR * 0.8) break;
                    const x = cx + Math.cos(theta) * r;
                    const y = cy + Math.sin(theta) * r;
                    if (first) { ctx.moveTo(x, y); first = false; }
                    else { ctx.lineTo(x, y); }
                }
                ctx.stroke();
            }
            ctx.restore();
        }
    };

    const drawConcentric = (ctx, cx, cy, maxR, rot, rng, w, h) => {
        const ringCount = frequency;
        const ringSpacing = maxR / ringCount;

        for (let i = 1; i <= ringCount; i++) {
            const r = i * ringSpacing;
            const jitterR = jitter > 0 ? (rng() - 0.5) * jitter * ringSpacing * 0.5 : 0;
            const actualR = Math.max(1, r + jitterR);

            ctx.beginPath();
            if (symmetry <= 2) {
                // Perfect circles
                ctx.arc(cx, cy, actualR, 0, Math.PI * 2);
            } else {
                // Polygonal rings
                const sides = symmetry;
                for (let s = 0; s <= sides; s++) {
                    const angle = (s / sides) * Math.PI * 2 + rot;
                    const x = cx + Math.cos(angle) * actualR;
                    const y = cy + Math.sin(angle) * actualR;
                    if (s === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
            }
            ctx.closePath();
            ctx.stroke();
        }
    };

    const drawGrid = (ctx, cx, cy, maxR, rot, rng, w, h) => {
        const cellCount = frequency;
        const cellW = w / cellCount;
        const cellH = h / cellCount;
        const jitterAmt = jitter * Math.min(cellW, cellH) * 0.4;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot);
        ctx.translate(-cx, -cy);

        // Draw vertical lines
        for (let i = 0; i <= cellCount; i++) {
            const x = i * cellW;
            const jx = jitterAmt > 0 ? (rng() - 0.5) * jitterAmt : 0;
            ctx.beginPath();
            ctx.moveTo(x + jx, 0);
            ctx.lineTo(x + jx, h);
            ctx.stroke();
        }

        // Draw horizontal lines
        for (let i = 0; i <= cellCount; i++) {
            const y = i * cellH;
            const jy = jitterAmt > 0 ? (rng() - 0.5) * jitterAmt : 0;
            ctx.beginPath();
            ctx.moveTo(0, y + jy);
            ctx.lineTo(w, y + jy);
            ctx.stroke();
        }

        // Diagonal lines if symmetry > 4
        if (symmetry > 4) {
            ctx.globalAlpha = 0.4;
            ctx.lineWidth = Math.max(0.5, lineWeight * 0.5);
            for (let i = -cellCount; i <= cellCount * 2; i++) {
                ctx.beginPath();
                ctx.moveTo(i * cellW, 0);
                ctx.lineTo(i * cellW + h, h);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(i * cellW, 0);
                ctx.lineTo(i * cellW - h, h);
                ctx.stroke();
            }
        }

        ctx.restore();
    };

    const drawWave = (ctx, cx, cy, maxR, rot, rng, w, h) => {
        const waveCount = frequency;
        const amp = amplitude * h * 0.15;
        const wl = wavelength * w * 0.1;
        const spacing = h / waveCount;

        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate(rot);
        ctx.translate(-cx, -cy);

        for (let i = 0; i < waveCount; i++) {
            const baseY = i * spacing;
            const jitterY = jitter > 0 ? (rng() - 0.5) * jitter * spacing * 0.3 : 0;

            ctx.beginPath();
            for (let x = 0; x <= w; x += 2) {
                const phase = (i * Math.PI * 0.5);
                const y = baseY + jitterY + Math.sin((x / wl) + phase) * amp;
                if (x === 0) ctx.moveTo(x, y);
                else ctx.lineTo(x, y);
            }
            ctx.stroke();

            // Secondary harmonic
            if (fillDensity > 0.3) {
                ctx.save();
                ctx.globalAlpha = fillDensity * 0.3;
                ctx.lineWidth = Math.max(0.5, lineWeight * 0.3);
                ctx.beginPath();
                for (let x = 0; x <= w; x += 2) {
                    const phase = (i * Math.PI * 0.5) + Math.PI;
                    const y = baseY + jitterY + spacing * 0.5 + Math.sin((x / (wl * 0.5)) + phase) * amp * 0.4;
                    if (x === 0) ctx.moveTo(x, y);
                    else ctx.lineTo(x, y);
                }
                ctx.stroke();
                ctx.restore();
            }
        }
        ctx.restore();
    };

    const drawRadial = (ctx, cx, cy, maxR, rot, rng, w, h) => {
        const segments = symmetry;
        const ringCount = frequency;
        const ringSpacing = maxR / ringCount;
        const segAngle = (Math.PI * 2) / segments;

        // Draw spokes
        for (let s = 0; s < segments; s++) {
            const angle = s * segAngle + rot;
            const jAngle = jitter > 0 ? (rng() - 0.5) * jitter * 0.1 : 0;
            ctx.beginPath();
            ctx.moveTo(cx, cy);
            ctx.lineTo(
                cx + Math.cos(angle + jAngle) * maxR,
                cy + Math.sin(angle + jAngle) * maxR
            );
            ctx.stroke();
        }

        // Draw rings
        for (let r = 1; r <= ringCount; r++) {
            const radius = r * ringSpacing;
            ctx.beginPath();
            ctx.arc(cx, cy, radius, 0, Math.PI * 2);
            ctx.stroke();
        }

        // Inner decorative elements
        if (fillDensity > 0.2) {
            ctx.save();
            ctx.globalAlpha = fillDensity * 0.5;
            for (let r = 1; r <= ringCount; r++) {
                const radius = r * ringSpacing;
                for (let s = 0; s < segments; s++) {
                    const angle = s * segAngle + rot + segAngle * 0.5;
                    const x = cx + Math.cos(angle) * (radius - ringSpacing * 0.5);
                    const y = cy + Math.sin(angle) * (radius - ringSpacing * 0.5);
                    const dotR = Math.min(ringSpacing * 0.15, lineWeight * 2);

                    ctx.beginPath();
                    ctx.arc(x, y, dotR, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
            ctx.restore();
        }

        // Petal arcs for mandala look
        if (amplitude > 0.2) {
            ctx.save();
            ctx.globalAlpha = amplitude * 0.6;
            ctx.lineWidth = Math.max(0.5, lineWeight * 0.6);
            for (let r = 2; r <= ringCount; r++) {
                const inner = (r - 1) * ringSpacing;
                const outer = r * ringSpacing;
                for (let s = 0; s < segments; s++) {
                    const a1 = s * segAngle + rot;
                    const a2 = (s + 1) * segAngle + rot;
                    const mid = (a1 + a2) / 2;

                    // Draw curved petal
                    const cp1x = cx + Math.cos(mid) * outer * 1.1;
                    const cp1y = cy + Math.sin(mid) * outer * 1.1;

                    ctx.beginPath();
                    ctx.moveTo(cx + Math.cos(a1) * inner, cy + Math.sin(a1) * inner);
                    ctx.quadraticCurveTo(cp1x, cp1y, cx + Math.cos(a2) * inner, cy + Math.sin(a2) * inner);
                    ctx.stroke();
                }
            }
            ctx.restore();
        }
    };

    const drawMoire = (ctx, cx, cy, maxR, rot, rng, w, h) => {
        const lineCount = frequency * 3;

        // First set: concentric circles
        ctx.lineWidth = Math.max(0.5, lineWeight * 0.7);
        const circSpacing = maxR / lineCount;
        for (let i = 1; i <= lineCount; i++) {
            ctx.beginPath();
            ctx.arc(cx, cy, i * circSpacing, 0, Math.PI * 2);
            ctx.stroke();
        }

        // Second set: offset concentric circles (creates moiré)
        const offsetX = cx + w * amplitude * 0.3;
        const offsetY = cy + h * wavelength * 0.2;

        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.translate(0, 0);
        ctx.rotate(rot);

        for (let i = 1; i <= lineCount; i++) {
            ctx.beginPath();
            ctx.arc(offsetX, offsetY, i * circSpacing, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.restore();

        // Third set if high density: parallel lines
        if (fillDensity > 0.3) {
            ctx.save();
            ctx.globalAlpha = fillDensity * 0.4;
            ctx.lineWidth = Math.max(0.3, lineWeight * 0.4);
            const lineSpacing = w / (lineCount * 0.8);
            for (let i = 0; i < lineCount; i++) {
                const x = i * lineSpacing;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, h);
                ctx.stroke();
            }
            ctx.restore();
        }
    };

    // ==================== FILL STYLE ENGINE ====================

    const applyFillStyle = (ctx, w, h, rng) => {
        if (fillStyle === 'solid') return; // No additional fill processing

        switch (fillStyle) {
            case 'dots': {
                // Add a dot grid texture over the pattern using source-atop
                // (only draws where there are existing strokes)
                ctx.save();
                ctx.globalCompositeOperation = 'source-atop';
                ctx.globalAlpha = 1;

                const dotSpacing = Math.max(3, Math.round(14 * (1 - fillDensity)));
                const dotRadius = dotSpacing * 0.3;

                ctx.fillStyle = ctx.strokeStyle || fgColor;
                for (let y = 0; y < h; y += dotSpacing) {
                    for (let x = 0; x < w; x += dotSpacing) {
                        ctx.beginPath();
                        ctx.arc(x + dotSpacing / 2, y + dotSpacing / 2, dotRadius, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
                ctx.restore();

                // Also add dots in the empty space at low opacity for texture
                ctx.save();
                ctx.globalCompositeOperation = 'source-over';
                ctx.globalAlpha = fillDensity * 0.15;
                ctx.fillStyle = fgColor;
                const bgDotSpacing = Math.max(4, Math.round(16 * (1 - fillDensity)));
                const bgDotRadius = bgDotSpacing * 0.25;
                for (let y = 0; y < h; y += bgDotSpacing) {
                    for (let x = 0; x < w; x += bgDotSpacing) {
                        ctx.beginPath();
                        ctx.arc(x + bgDotSpacing / 2, y + bgDotSpacing / 2, bgDotRadius, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
                ctx.restore();
                break;
            }
            case 'crosshatch': {
                // Add crosshatch lines as texture over and around the geometry
                const hatchSpacing = Math.max(3, Math.round(12 * (1 - fillDensity)));

                // Crosshatch in the stroke areas (source-atop)
                ctx.save();
                ctx.globalCompositeOperation = 'source-atop';
                ctx.strokeStyle = fgColor;
                ctx.lineWidth = 0.8;
                ctx.globalAlpha = 0.9;

                for (let i = -h; i < w + h; i += hatchSpacing) {
                    ctx.beginPath();
                    ctx.moveTo(i, 0);
                    ctx.lineTo(i + h, h);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(i + h, 0);
                    ctx.lineTo(i, h);
                    ctx.stroke();
                }
                ctx.restore();

                // Background crosshatch for atmosphere
                ctx.save();
                ctx.globalCompositeOperation = 'source-over';
                ctx.globalAlpha = fillDensity * 0.08;
                ctx.strokeStyle = fgColor;
                ctx.lineWidth = 0.5;

                for (let i = -h; i < w + h; i += hatchSpacing * 1.5) {
                    ctx.beginPath();
                    ctx.moveTo(i, 0);
                    ctx.lineTo(i + h, h);
                    ctx.stroke();
                    ctx.beginPath();
                    ctx.moveTo(i + h, 0);
                    ctx.lineTo(i, h);
                    ctx.stroke();
                }
                ctx.restore();
                break;
            }
            case 'dither': {
                // Bayer 4x4 ordered dither — punch holes in the geometry
                const patternData = ctx.getImageData(0, 0, w, h);
                const d = patternData.data;
                const bayerSize = 4;
                const bayer = [
                    [0, 8, 2, 10],
                    [12, 4, 14, 6],
                    [3, 11, 1, 9],
                    [15, 7, 13, 5],
                ];
                const threshold = (1 - fillDensity) * 16;

                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        const idx = (y * w + x) * 4;
                        if (d[idx + 3] === 0) continue;
                        const bayerVal = bayer[y % bayerSize][x % bayerSize];
                        if (bayerVal < threshold) {
                            d[idx + 3] = 0;
                        }
                    }
                }
                ctx.clearRect(0, 0, w, h);
                ctx.putImageData(patternData, 0, 0);
                break;
            }
            case 'noise': {
                const patternData = ctx.getImageData(0, 0, w, h);
                const d = patternData.data;
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        const idx = (y * w + x) * 4;
                        if (d[idx + 3] === 0) continue;
                        if (rng() > fillDensity) {
                            d[idx + 3] = 0;
                        }
                    }
                }
                ctx.clearRect(0, 0, w, h);
                ctx.putImageData(patternData, 0, 0);
                break;
            }
        }
    };

    // ==================== FLOOD FILL ENGINE ====================

    const floodFill = (ctx, startX, startY, w, h, color) => {
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;
        const sx = Math.round(startX);
        const sy = Math.round(startY);
        if (sx < 0 || sx >= w || sy < 0 || sy >= h) return;

        const startIdx = (sy * w + sx) * 4;
        const startR = data[startIdx];
        const startG = data[startIdx + 1];
        const startB = data[startIdx + 2];
        const startA = data[startIdx + 3];

        // Parse fill color
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = 1; tempCanvas.height = 1;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = color;
        tempCtx.fillRect(0, 0, 1, 1);
        const cData = tempCtx.getImageData(0, 0, 1, 1).data;
        const fillR = cData[0], fillG = cData[1], fillB = cData[2], fillA = cData[3];

        // Don't fill if target is same as fill color
        if (startR === fillR && startG === fillG && startB === fillB && startA === fillA) return;

        const tolerance = 30;
        const matches = (idx) => {
            return Math.abs(data[idx] - startR) <= tolerance &&
                Math.abs(data[idx + 1] - startG) <= tolerance &&
                Math.abs(data[idx + 2] - startB) <= tolerance &&
                Math.abs(data[idx + 3] - startA) <= tolerance;
        };

        // Scanline flood fill
        const stack = [[sx, sy]];
        const visited = new Uint8Array(w * h);

        while (stack.length > 0) {
            const [x, y] = stack.pop();
            if (x < 0 || x >= w || y < 0 || y >= h) continue;
            const pos = y * w + x;
            if (visited[pos]) continue;
            const idx = pos * 4;
            if (!matches(idx)) continue;

            // Scan left
            let leftX = x;
            while (leftX > 0 && !visited[y * w + leftX - 1] && matches((y * w + leftX - 1) * 4)) leftX--;

            // Scan right
            let rightX = x;
            while (rightX < w - 1 && !visited[y * w + rightX + 1] && matches((y * w + rightX + 1) * 4)) rightX++;

            // Fill the scanline and check above/below
            for (let fx = leftX; fx <= rightX; fx++) {
                const fpos = y * w + fx;
                visited[fpos] = 1;
                const fidx = fpos * 4;
                data[fidx] = fillR;
                data[fidx + 1] = fillG;
                data[fidx + 2] = fillB;
                data[fidx + 3] = fillA;

                if (y > 0 && !visited[(y - 1) * w + fx]) stack.push([fx, y - 1]);
                if (y < h - 1 && !visited[(y + 1) * w + fx]) stack.push([fx, y + 1]);
            }
        }

        ctx.putImageData(imageData, 0, 0);
        return visited; // Return the mask of filled pixels
    };

    // Apply texture fill within a masked region
    const texturedFill = (ctx, w, h, visited, style, color, density) => {
        if (style === 'solid') return; // Already filled by floodFill

        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        // Parse fill color for reference
        const tmpC = document.createElement('canvas');
        tmpC.width = 1; tmpC.height = 1;
        const tmpCtx = tmpC.getContext('2d');
        tmpCtx.fillStyle = color;
        tmpCtx.fillRect(0, 0, 1, 1);
        const cD = tmpCtx.getImageData(0, 0, 1, 1).data;
        const fR = cD[0], fG = cD[1], fB = cD[2];

        switch (style) {
            case 'dots': {
                const dotSpacing = Math.max(3, Math.round(14 * (1 - density)));
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        const pos = y * w + x;
                        if (!visited[pos]) continue;
                        // Keep pixel only if within a dot radius
                        const cx = (x % dotSpacing) - dotSpacing / 2;
                        const cy = (y % dotSpacing) - dotSpacing / 2;
                        const dotR = dotSpacing * 0.35;
                        if (cx * cx + cy * cy > dotR * dotR) {
                            const idx = pos * 4;
                            data[idx + 3] = 0; // Make transparent (remove fill outside dots)
                        }
                    }
                }
                break;
            }
            case 'crosshatch': {
                const spacing = Math.max(3, Math.round(10 * (1 - density)));
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        const pos = y * w + x;
                        if (!visited[pos]) continue;
                        // Keep pixel only if on a hatch line
                        const onForward = ((x + y) % spacing) < 1;
                        const onBackward = ((x - y + spacing * 100) % spacing) < 1;
                        if (!onForward && !onBackward) {
                            const idx = pos * 4;
                            data[idx + 3] = 0;
                        }
                    }
                }
                break;
            }
            case 'dither': {
                const bayerSize = 4;
                const bayer = [
                    [0, 8, 2, 10],
                    [12, 4, 14, 6],
                    [3, 11, 1, 9],
                    [15, 7, 13, 5],
                ];
                const threshold = (1 - density) * 16;
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        const pos = y * w + x;
                        if (!visited[pos]) continue;
                        if (bayer[y % bayerSize][x % bayerSize] < threshold) {
                            const idx = pos * 4;
                            data[idx + 3] = 0;
                        }
                    }
                }
                break;
            }
            case 'noise': {
                const rng2 = seededRandom(seed + 999);
                for (let y = 0; y < h; y++) {
                    for (let x = 0; x < w; x++) {
                        const pos = y * w + x;
                        if (!visited[pos]) continue;
                        if (rng2() > density) {
                            const idx = pos * 4;
                            data[idx + 3] = 0;
                        }
                    }
                }
                break;
            }
        }
        ctx.putImageData(imageData, 0, 0);
    };

    const reapplyFills = (ctx, w, h) => {
        const ops = fillOpsRef.current;
        ops.forEach(op => {
            const px = Math.round(op.nx * w);
            const py = Math.round(op.ny * h);
            const mask = floodFill(ctx, px, py, w, h, op.color);
            if (mask && op.style && op.style !== 'solid') {
                texturedFill(ctx, w, h, mask, op.style, op.color, op.density || 0.5);
            }
        });
    };

    const handleCanvasClick = useCallback((e) => {
        if (!fillToolActive || !previewCanvasRef.current) return;
        const canvas = previewCanvasRef.current;
        const rect = canvas.getBoundingClientRect();

        // Map display coords to canvas coords
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        const x = (e.clientX - rect.left) * scaleX;
        const y = (e.clientY - rect.top) * scaleY;

        const ctx = canvas.getContext('2d');
        const mask = floodFill(ctx, x, y, canvas.width, canvas.height, fillColor);

        // Apply texture fill if not solid
        if (mask && fillStyle !== 'solid') {
            texturedFill(ctx, canvas.width, canvas.height, mask, fillStyle, fillColor, fillDensity);
        }

        // Store as normalized coordinates so fills survive re-renders
        const newOp = {
            nx: x / canvas.width,
            ny: y / canvas.height,
            color: fillColor,
            style: fillStyle,
            density: fillDensity,
        };
        fillOpsRef.current = [...fillOpsRef.current, newOp];
        setFillOperations(prev => [...prev, newOp]);
    }, [fillToolActive, fillColor, fillStyle, fillDensity]);

    const undoLastFill = () => {
        fillOpsRef.current = fillOpsRef.current.slice(0, -1);
        setFillOperations(prev => prev.slice(0, -1));
    };

    const clearAllFills = () => {
        fillOpsRef.current = [];
        setFillOperations([]);
    };

    // ==================== ACTIONS ====================

    const handleApply = useCallback(() => {
        const fullCanvas = document.createElement('canvas');
        const img = sourceImgRef.current;
        if (img) {
            doRender(fullCanvas, Math.max(img.width, img.height));
        } else {
            doRender(fullCanvas, 1024);
        }
        const dataUrl = fullCanvas.toDataURL('image/png');
        if (isEffectMode) {
            onApply?.(dataUrl);
        } else {
            onAddAsLayer?.(dataUrl);
        }
    }, [geometryMode, frequency, rotation, scale, lineWeight, symmetry,
        amplitude, wavelength, twist, arms, jitter, fillStyle, fillDensity,
        fgColor, bgColor, transparentBg, useGradient, gradientEnd, gradientAngle, blendMode, blendOpacity, seed,
        fillOperations, onApply, onAddAsLayer, isEffectMode]);

    // ==================== UI HELPERS ====================

    const sliderRow = (label, min, max, value, onChange, step = 1) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80, whiteSpace: 'nowrap' }}>{label}</Text>
            <Slider min={min} max={max} step={step} value={value} onChange={onChange}
                style={{ flex: 1, margin: 0 }}
                styles={{
                    track: { background: '#c084fc' },
                    rail: { background: '#30363d' },
                    handle: { borderColor: '#c084fc', width: 12, height: 12 }
                }}
            />
            <Text style={{ color: '#c084fc', fontSize: 11, fontFamily: 'monospace', minWidth: 36, textAlign: 'right' }}>
                {step < 1 ? value.toFixed(2) : value}
            </Text>
        </div>
    );

    const toggleRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <Switch checked={value} onChange={onChange} size="small" style={{ background: value ? '#c084fc' : '#30363d' }} />
        </div>
    );

    const colorRow = (label, value, onChange) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Text style={{ color: '#8b949e', fontSize: 11, minWidth: 80 }}>{label}</Text>
            <div style={{ position: 'relative' }}>
                <div style={{
                    width: 24, height: 24, borderRadius: 4,
                    background: value, border: '1px solid #30363d', cursor: 'pointer',
                }} onClick={() => document.getElementById(`color-${label}`).click()} />
                <input
                    id={`color-${label}`}
                    type="color"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{ position: 'absolute', top: 0, left: 0, width: 24, height: 24, opacity: 0, cursor: 'pointer' }}
                />
            </div>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                size="small"
                style={{
                    width: 80,
                    background: '#21262d',
                    borderColor: '#30363d',
                    color: '#c9d1d9',
                    fontSize: 11,
                    fontFamily: 'monospace',
                }}
            />
        </div>
    );

    if (!visible) return null;

    return (
        <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 110, display: 'flex',
            background: 'rgba(13, 17, 23, 0.95)',
            pointerEvents: 'auto',
        }}
            onClick={(e) => e.stopPropagation()}>

            {/* Preview Area */}
            <div style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24, overflow: 'hidden',
            }}>
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <Spin size="large" />
                        <Text style={{ color: '#8b949e' }}>Loading image...</Text>
                    </div>
                ) : (
                    <canvas ref={previewCanvasRef}
                        onClick={handleCanvasClick}
                        style={{
                            maxWidth: '100%', maxHeight: '100%',
                            borderRadius: 8,
                            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
                            border: fillToolActive ? '2px solid #c084fc' : '1px solid #30363d',
                            cursor: fillToolActive ? 'crosshair' : 'default',
                        }} />
                )}
            </div>

            {/* Controls Sidebar */}
            <div style={{
                width: 320, flexShrink: 0,
                borderLeft: '1px solid #30363d',
                display: 'flex', flexDirection: 'column',
                background: '#0d1117',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', borderBottom: '1px solid #30363d',
                    background: '#161b22', flexShrink: 0,
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 16 }}>⬡</span>
                        <span style={{ color: '#c9d1d9', fontWeight: 600, fontSize: 14 }}>
                            Pattern {isEffectMode ? '(Effect)' : '(Generate)'}
                        </span>
                    </div>
                    <Button type="text" size="small" onClick={onClose}
                        style={{ color: '#8b949e', fontSize: 12, padding: '2px 6px' }}>✕</Button>
                </div>

                {/* Scrollable Controls */}
                <div style={{
                    flex: 1, overflowY: 'auto', padding: '12px 14px',
                    display: 'flex', flexDirection: 'column', gap: 16,
                }}>

                    {/* Geometry Mode */}
                    <div>
                        <Text style={{ color: '#c084fc', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Geometry
                        </Text>
                        <div style={{
                            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 4, marginTop: 8,
                        }}>
                            {GEOMETRY_MODES.map((mode) => (
                                <button
                                    key={mode.value}
                                    onClick={() => setGeometryMode(mode.value)}
                                    style={{
                                        display: 'flex', flexDirection: 'column',
                                        alignItems: 'center', gap: 2,
                                        padding: '6px 4px', borderRadius: 6,
                                        background: geometryMode === mode.value ? 'rgba(192, 132, 252, 0.2)' : '#21262d',
                                        border: geometryMode === mode.value ? '1px solid #c084fc' : '1px solid #30363d',
                                        color: geometryMode === mode.value ? '#c084fc' : '#8b949e',
                                        cursor: 'pointer', fontSize: 10, fontWeight: 500,
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    <span style={{ fontSize: 16 }}>{mode.icon}</span>
                                    {mode.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Shape Controls */}
                    <div>
                        <Text style={{ color: '#c084fc', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Shape
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {sliderRow('Frequency', 2, 40, frequency, setFrequency)}
                            {sliderRow('Rotation', 0, 360, rotation, setRotation)}
                            {sliderRow('Scale', 0.2, 3.0, scale, (v) => setScale(v), 0.05)}
                            {sliderRow('Line Weight', 0.5, 8, lineWeight, (v) => setLineWeight(v), 0.5)}
                            {sliderRow('Jitter', 0, 1, jitter, (v) => setJitter(v), 0.01)}

                            {(geometryMode === 'spiral') && (
                                <>
                                    {sliderRow('Twist', 0.5, 10, twist, (v) => setTwist(v), 0.1)}
                                    {sliderRow('Arms', 1, 12, arms, setArms)}
                                </>
                            )}

                            {(geometryMode === 'concentric' || geometryMode === 'radial' || geometryMode === 'grid') && (
                                sliderRow('Symmetry', 2, 24, symmetry, setSymmetry)
                            )}

                            {(geometryMode === 'wave' || geometryMode === 'moire') && (
                                <>
                                    {sliderRow('Amplitude', 0.05, 2.0, amplitude, (v) => setAmplitude(v), 0.05)}
                                    {sliderRow('Wavelength', 0.05, 2.0, wavelength, (v) => setWavelength(v), 0.05)}
                                </>
                            )}

                            {geometryMode === 'radial' && (
                                sliderRow('Amplitude', 0, 1, amplitude, (v) => setAmplitude(v), 0.01)
                            )}
                        </div>
                    </div>

                    {/* Fill Style */}
                    <div>
                        <Text style={{ color: '#c084fc', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Fill Style
                        </Text>
                        <div style={{
                            display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap',
                        }}>
                            {FILL_STYLES.map((fs) => (
                                <button
                                    key={fs.value}
                                    onClick={() => setFillStyle(fs.value)}
                                    style={{
                                        padding: '4px 10px', borderRadius: 4,
                                        background: fillStyle === fs.value ? 'rgba(192, 132, 252, 0.2)' : '#21262d',
                                        border: fillStyle === fs.value ? '1px solid #c084fc' : '1px solid #30363d',
                                        color: fillStyle === fs.value ? '#c084fc' : '#8b949e',
                                        cursor: 'pointer', fontSize: 11, fontWeight: 500,
                                        transition: 'all 0.15s ease',
                                    }}
                                >
                                    {fs.label}
                                </button>
                            ))}
                        </div>
                        {fillStyle !== 'solid' && (
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Density', 0.1, 1, fillDensity, (v) => setFillDensity(v), 0.01)}
                            </div>
                        )}
                    </div>

                    {/* Colors */}
                    <div>
                        <Text style={{ color: '#c084fc', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Color
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {colorRow('Foreground', fgColor, setFgColor)}
                            {!isEffectMode && colorRow('Background', bgColor, setBgColor)}
                            {!isEffectMode && toggleRow('Transparent', transparentBg, setTransparentBg)}
                            {toggleRow('Gradient', useGradient, setUseGradient)}
                            {useGradient && colorRow('Gradient End', gradientEnd, setGradientEnd)}
                            {useGradient && sliderRow('Angle', 0, 360, gradientAngle, setGradientAngle)}
                        </div>

                        {/* Palette presets */}
                        {palettes.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <Text style={{ color: '#6e7681', fontSize: 10 }}>Palettes</Text>
                                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
                                    {palettes.slice(0, 6).map((palette, pi) => (
                                        <button
                                            key={pi}
                                            onClick={() => {
                                                const colors = palette.colors || [];
                                                if (colors.length >= 1) setFgColor(colors[0]);
                                                if (colors.length >= 2) setBgColor(colors[1]);
                                                if (colors.length >= 3) { setUseGradient(true); setGradientEnd(colors[2]); }
                                            }}
                                            style={{
                                                display: 'flex', gap: 1, padding: 2,
                                                background: '#21262d', border: '1px solid #30363d',
                                                borderRadius: 4, cursor: 'pointer',
                                            }}
                                        >
                                            {(palette.colors || []).slice(0, 4).map((c, ci) => (
                                                <div key={ci} style={{
                                                    width: 12, height: 12, background: c,
                                                    borderRadius: ci === 0 ? '2px 0 0 2px' : ci === (palette.colors || []).slice(0, 4).length - 1 ? '0 2px 2px 0' : 0,
                                                }} />
                                            ))}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Blend (effect mode only) */}
                    {isEffectMode && (
                        <div>
                            <Text style={{ color: '#c084fc', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Blend
                            </Text>
                            <Select value={blendMode} onChange={setBlendMode}
                                style={{ width: '100%', marginTop: 6 }}
                                options={BLEND_MODES}
                            />
                            <div style={{ marginTop: 8 }}>
                                {sliderRow('Opacity', 0, 1, blendOpacity, (v) => setBlendOpacity(v), 0.01)}
                            </div>
                        </div>
                    )}

                    {/* Fill Tool */}
                    <div>
                        <Text style={{ color: '#c084fc', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Fill Tool
                        </Text>
                        <div style={{ marginTop: 8 }}>
                            {toggleRow('Active', fillToolActive, setFillToolActive)}
                            {fillToolActive && (
                                <>
                                    {colorRow('Fill Color', fillColor, setFillColor)}
                                    <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
                                        <Button size="small" onClick={undoLastFill}
                                            disabled={fillOperations.length === 0}
                                            style={{
                                                background: '#21262d', borderColor: '#30363d',
                                                color: fillOperations.length > 0 ? '#c9d1d9' : '#484f58',
                                                fontSize: 11, flex: 1,
                                            }}>
                                            ↩ Undo ({fillOperations.length})
                                        </Button>
                                        <Button size="small" onClick={clearAllFills}
                                            disabled={fillOperations.length === 0}
                                            style={{
                                                background: '#21262d', borderColor: '#30363d',
                                                color: fillOperations.length > 0 ? '#f85149' : '#484f58',
                                                fontSize: 11, flex: 1,
                                            }}>
                                            ✕ Clear All
                                        </Button>
                                    </div>
                                    <Text style={{ color: '#484f58', fontSize: 10, marginTop: 6, display: 'block' }}>
                                        Click on the pattern to fill enclosed regions
                                    </Text>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Re-roll */}
                    <Button size="small" type="text"
                        onClick={() => setSeed(Date.now())}
                        style={{ color: '#6e7681', fontSize: 11, alignSelf: 'flex-start' }}>
                        🎲 Re-roll Seed
                    </Button>
                </div>

                {/* Action Footer */}
                <div style={{
                    padding: '12px 14px', borderTop: '1px solid #30363d',
                    display: 'flex', gap: 8, flexShrink: 0, background: '#161b22',
                }}>
                    <Button onClick={onClose}
                        style={{ flex: 1, background: '#21262d', borderColor: '#30363d', color: '#c9d1d9' }}>
                        Cancel
                    </Button>
                    <Button type="primary" onClick={handleApply}
                        style={{ flex: 1, background: '#c084fc', borderColor: '#c084fc', color: '#000' }}>
                        {isEffectMode ? 'Apply' : 'Add as Layer'}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PatternEditorPanel;
